/**
 * Mobile soft-keyboard fallback for browsers that don't honor the
 * `interactive-widget=resizes-content` viewport hint (older iOS Safari,
 * embedded WebViews). Mirrors `window.visualViewport.height` into a CSS
 * variable `--app-vh` so layouts can opt-in via
 * `min-h-[var(--app-vh,100dvh)]` and follow the visible viewport when the
 * keyboard appears or hides.
 *
 * Mounted once at the app root. No-ops on browsers without
 * `window.visualViewport` (every modern mobile browser exposes it).
 *
 * @module hooks/use-visual-viewport
 */

import { useEffect } from 'react';

/**
 * Sync the visual viewport height to a CSS custom property.
 * Reflects keyboard show/hide and URL-bar collapse without JS layout work.
 */
export function useVisualViewport() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const root = document.documentElement;

    const sync = () => {
      // The visual viewport reflects the area NOT covered by the soft
      // keyboard. We expose it as a px value so consumers can use it in
      // `calc()` or as a min-height fallback.
      root.style.setProperty('--app-vh', `${vv.height}px`);

      // When the keyboard opens, iOS (Safari AND Chrome/WebView) doesn't just
      // shrink the visual viewport — it also PANS it down, pushing the top of
      // the layout viewport (our app bar + hero) above the visible area while
      // it tries to reveal the focused input. The app shell is sized to the
      // visible area via `--app-vh`, so the input is already reachable and the
      // pan is pure breakage: the navbar and icons scroll off the top. Publish
      // the pan offset so the shell can translate back into the visible
      // rectangle, and undo any document-level scroll the browser introduced.
      root.style.setProperty('--app-vv-offset-top', `${vv.offsetTop}px`);

      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    // The document itself can be scrolled by the browser's focus-into-view even
    // though html/body are `overflow: hidden`; pin it back to the top.
    window.addEventListener('scroll', sync, { passive: true });
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      window.removeEventListener('scroll', sync);
    };
  }, []);
}
